import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { catchError, map } from 'rxjs';
import { DropDownList } from 'src/app/admin/models/common/drop-down-list';
import { CreateOrUpdateUserDto } from 'src/app/admin/models/user-management-model';
import { UserManagementService } from 'src/app/admin/services/user-management.service';
import { BaseComponent } from 'src/app/shared/base/base-component';

@Component({
  selector: 'app-create-or-edit-user',
  templateUrl: './create-or-edit-user.component.html',
  styleUrls: ['./create-or-edit-user.component.css']
})
export class CreateOrEditUserComponent extends BaseComponent implements OnInit, OnDestroy {
  @Input() user: CreateOrUpdateUserDto;

  public userTypes: DropDownList<number, string>[];
  public permissions: DropDownList<string, string>[];
  public displayedPermissions: DropDownList<string, string>[];

  private nameRegex = /^[a-zA-Z-\u0621-\u064A]+$/;
  private phoneRegex = /^(009665|9665|\+9665|05|5)(5|0|3|6|4|9|1|8|7)([0-9]{7})$/;
  private jobIdRegex = /^(?=\d{3,10}$)\d+$/;
  private emailRegex = "[a-zA-Z0-9.-_]{1,}@[a-zA-Z.-]{2,}[.]{1}[a-zA-Z]{2,}";

  constructor(
    public activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private userManagementService: UserManagementService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.createForm();

    this.getUserTypes();
    this.getPermissions();
  }
  ngOnDestroy(): void {
    this.subscription.forEach((sb) => sb.unsubscribe());
  }

  public getUserTypes(): void {
    const sub = this.userManagementService.GetUserTypesDDL()
      .pipe(
        map((response: DropDownList<number, string>[]) => {
          if (response) {
            this.userTypes = response;
          }
        }),
        catchError((err) => {
          return this.logException(err);
        })
      )
      .subscribe();

    this.subscription.push(sub);
  }
  public getPermissions(): void {
    const sub = this.userManagementService.GetPermissionsDDL()
      .pipe(
        map((response: DropDownList<string, string>[]) => {
          if (response) {
            this.permissions = response;
            this.displayedPermissions = response;
          }
        }),
        catchError((err) => {
          return this.logException(err);
        })
      )
      .subscribe();

    this.subscription.push(sub);
  }
  public save() {
    this.submitted = true;
    if (this.formGroup.valid) {
      if (!this.user)
        this.user = {} as CreateOrUpdateUserDto;

      const data = this.form_Group.controls;

      this.user.name = data['name'].value;
      this.user.surname = data['surname'].value;
      this.user.email = data['email'].value;
      this.user.mobile = data['mobile'].value;
      this.user.userType = data['userType'].value;
      this.user.jobID = data['jobId'].value;
      this.user.permissions = data['permissions'].value;

      const action = !this.user.id ? this.userManagementService.Create(this.user) : this.userManagementService.Update(this.user);
      const sub = action.subscribe(() => {
        const action = !this.user.id ? this.translate.get('CreateOrUpdateUser.AddedSuccessfully') : this.translate.get('CreateOrUpdateUser.UpdatedSuccessfully');
        action.subscribe(msg => this.showSuccessMessage(msg));
        this.resetForm();
        this.dismiss();
      }, error => {
        this.submitted = false;
        if (error.status === 403)
          this.translate.get(`CreateOrUpdateUser.${error.error.error.message}`).subscribe(msg => this.showErrorMessage(msg));
        else
          this.translate.get('ErrorCodes.CreateError').subscribe(msg => this.showErrorMessage(msg));

        return this.logException(error);
      });

      this.subscription.push(sub);
    }
  }
  public dismiss() {
    this.submitted = false;
    this.createForm();

    this.activeModal.close(true);
  }
  public selectPermission($event: DropDownList<string, string>[]) {
    const selectedPermissionIds = $event.map(item => item.id);

    this.displayedPermissions = this.permissions.filter(permission => {
      return !selectedPermissionIds.includes(permission.id);
    });
  }

  private createForm() {
    this.formGroup = this.fb.group({
      name: [this.user?.name],
      surname: [this.user?.surname],
      email: [this.user?.email],
      mobile: [this.user?.mobile],
      userType: [this.user?.userType],
      jobId: [this.user?.jobID],
      permissions: [this.user?.permissions],
    });

    this.formGroup.statusChanges.subscribe(() => {/* console.log("test ", result)*/ });

    this.setCustomValidators();
  }
  private setCustomValidators() {
    const name: any | undefined = this.formGroup.get("name");
    const surname: any | undefined = this.formGroup.get("surname");
    const email: any | undefined = this.formGroup.get("email");
    const mobile: any | undefined = this.formGroup.get("mobile");
    const userType: any | undefined = this.formGroup.get("userType");
    const jobId: any | undefined = this.formGroup.get("jobId");
    const permissions: any | undefined = this.formGroup.get("permissions");

    name.setValidators([
      Validators.required,
      Validators.minLength(3),
      Validators.maxLength(20),
      Validators.pattern(this.nameRegex),
    ]);
    surname.setValidators([
      Validators.required,
      Validators.minLength(3),
      Validators.maxLength(20),
      Validators.pattern(this.nameRegex),
    ]);
    email.setValidators([
      Validators.required,
      Validators.minLength(4),
      Validators.maxLength(60),
      Validators.pattern(this.emailRegex),
    ]);
    mobile.setValidators([
      Validators.required,
      Validators.minLength(10),
      Validators.maxLength(15),
      Validators.pattern(this.phoneRegex),
    ]);
    userType.setValidators([
      Validators.required,
      Validators.min(1),
    ]);
    jobId.setValidators([
      Validators.required,
      Validators.minLength(3),
      Validators.maxLength(10),
      Validators.pattern(this.jobIdRegex),
    ]);
    permissions.setValidators([
      Validators.required
    ]);

    name.updateValueAndValidity();
    surname.updateValueAndValidity();
    email.updateValueAndValidity();
    mobile.updateValueAndValidity();
    userType.updateValueAndValidity();
    jobId.updateValueAndValidity();
    permissions.updateValueAndValidity();
  }
  private resetForm() {
    this.submitted = false;
    this.createForm();
  }
}
